import * as React from 'react';
import { connect } from "react-redux";
import { IGenericUserProps, genericMapStateToProps } from '../../DataClasses/GenericUser';
import { createStyles, Theme } from '@mui/material/styles';;
import { withStyles, WithStyles } from '@mui/styles';;
import idssStyle from "../../Configuration/SharedStyling";
import { SubmissionType } from '../../DataClasses/SharedData';
import { RouteComponentProps } from 'react-router';
import { Grid2 as Grid, Typography, Card, CardContent, Stepper, Step, StepLabel, StepContent, Fab, Fade } from '@mui/material';
import { AjaxError } from 'rxjs/ajax';
import { firstValueFrom } from 'rxjs';
import SubmissionsService from '../Services/SubmissionsService';
import { ISelectedSubmissions, ISelectedSubmissionsHolder } from '../../DataClasses/SubmissionData';
import { SeedTime } from "../../Configuration/Setting";
import { Redirect } from "react-router-dom";
import AdministrationService from "../Services/AdministrationService";
import { HOS_ENABLE_INTERIM_SUBMISSION } from "../Shared/Constants";
import SharedMaterialReactTable, { ISharedTableColumn } from "../Shared/MaterialReactTable";
import LoadingDataTable from '../Shared/LoadingDataTable';

const styles = (theme: Theme) => createStyles({
    ...idssStyle(theme),
    root: {
        width: '90%',
    },
    button: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    actionsContainer: {
        marginBottom: theme.spacing(2),
    },
    resetContainer: {
        padding: theme.spacing(3),
    }
});

interface ILocalState {
    isOrgsLoading: boolean;
    isSubsLoading: boolean;
    isSaved: boolean;
    currentStep: number;
    orgData: any[];
    selectedOrganizationIndexes: number[];
    submissionData: any[];
    selectedSubmissionIndexes: number[];
    markFinalType: SubmissionType;
    markFinalInProgress: boolean;
    redirect: boolean;
    enableHosInterimDataSubmission: boolean;
}

type IStylesProps = WithStyles<typeof styles>;

class BulkMarkFinalView extends React.Component<IGenericUserProps & IStylesProps & RouteComponentProps<{ markFinalType: SubmissionType.HEDIS | SubmissionType.CAHPS | SubmissionType.HOS }>, ILocalState> {

    constructor(props: any) {
        super(props);

        this.state = {
            isOrgsLoading: true,
            isSubsLoading: false,
            isSaved: false,
            currentStep: 0,
            orgData: [],
            selectedOrganizationIndexes: [],
            submissionData: [],
            selectedSubmissionIndexes: [],
            markFinalType: this.props.match.params.markFinalType,
            markFinalInProgress: false,
            redirect: false,
            enableHosInterimDataSubmission: false
        };
    }

    public async componentDidMount() {
        // This method runs when the component is first added to the page
        await this.RefreshModel();
    }

    public async componentDidUpdate(prevProps) {
        if (this.props.match.params.markFinalType !== prevProps.match.params.markFinalType) {
            this.ResetState();
        }
    }

    private redirectToErrorPage() {
        return <Redirect to='/errorpage' />;
    }

    private setRedirectState() {
        this.setState({ redirect: true });
    }

    private validationStatusBodyTemplate = (rowData: any) => {
        const { classes } = this.props;
        if (rowData.validationStatus === 'NoIssues')
            return <div className={`${classes.Default} ${classes.Success}`}>{rowData.validationStatus}</div>;
        else
            return rowData.validationStatus;
    };

    private isAttestedBodyTemplate = (rowData: any) => {
        const { classes } = this.props;
        if (rowData.isAttested === "Yes")
            return <div className={`${classes.Default} ${classes.Success}`}>{rowData.isAttested}</div>;
        else
            return rowData.isAttested;
    };

    private isFinalBodyTemplate = (rowData: any) => {
        const { classes } = this.props;
        if (rowData.isFinal === "No")
            return <div className={`${classes.Default} ${classes.Warning}`}>{rowData.isFinal}</div>;
        else
            return rowData.isFinal;
    };

    private getBulkMarkFinalColumns = (): ISharedTableColumn<any>[] => {
        const columns: ISharedTableColumn<any>[] = [
            {
                accessorKey: 'submissionId' as any,
                header: 'Sub ID',
                enableSorting: true,
                enableFiltering: true,
                size: 80
            },
            {
                accessorKey: 'productLine' as any,
                header: 'Product Line',
                enableSorting: true,
                enableFiltering: true,
                size: 120
            },
            {
                accessorKey: 'component' as any,
                header: 'Component',
                enableSorting: true,
                enableFiltering: true,
                size: 110
            },
            {
                accessorKey: 'orgId' as any,
                header: 'Org ID',
                enableSorting: true,
                enableFiltering: true,
                size: 80
            },
            {
                accessorKey: 'orgName' as any,
                header: 'Org Name',
                enableSorting: true,
                enableFiltering: true,
            },
            {
                accessorKey: 'isFinal' as any,
                header: 'IsFinal',
                enableSorting: true,
                enableFiltering: true,
                Cell: ({ row }) => this.isFinalBodyTemplate(row.original),
            },
        ];

        // Add conditional columns based on submission type
        if (this.state.markFinalType !== SubmissionType.HOS) {
            columns.splice(5, 0, {
                accessorKey: 'validationStatus' as any,
                header: 'Validation Status',
                enableSorting: true,
                enableFiltering: true,
                Cell: ({ row }) => this.validationStatusBodyTemplate(row.original),
            });
        }

        if (this.state.markFinalType === SubmissionType.HOS) {
            columns.splice(5, 0, {
                accessorKey: 'isAttested' as any,
                header: 'IsAttested',
                enableSorting: true,
                enableFiltering: true,
                Cell: ({ row }) => this.isAttestedBodyTemplate(row.original),
            });
        }

        return columns;
    };

    private getRowSelectionState = (): Record<string, boolean> => {
        const selection: Record<string, boolean> = {};
        this.state.selectedSubmissionIndexes.forEach(index => {
            if (index < this.state.submissionData.length) {
                const submission = this.state.submissionData[index];
                const id = `${submission.submissionId}_${submission.component}`;
                if (id) {
                    selection[id.toString()] = true;
                }
            }
        });
        return selection;
    };

    private isRowSelectable = (rowData: any) => {
        const isSelectable = this.state.markFinalType === SubmissionType.HOS
            ? (rowData.isAttested === "Yes" || this.state.enableHosInterimDataSubmission)
            && (rowData.validationStatus === "NoIssues" || rowData.validationStatus === "Warning")
            && rowData.isFinal === "No"
            : rowData.validationStatus === "NoIssues" && rowData.isFinal === "No";

        console.log('BulkMarkFinal isRowSelectable:', {
            submissionId: rowData.submissionId,
            markFinalType: this.state.markFinalType,
            isAttested: rowData.isAttested,
            validationStatus: rowData.validationStatus,
            isFinal: rowData.isFinal,
            enableHosInterimDataSubmission: this.state.enableHosInterimDataSubmission,
            result: isSelectable
        });

        return isSelectable;
    };

    public render() {
        console.log('BulkMarkFinal render: Checking isRowSelectable method:', {
            hasIsRowSelectable: !!this.isRowSelectable,
            isRowSelectableType: typeof this.isRowSelectable,
            isRowSelectableMethod: this.isRowSelectable
        });

        if (this.state.redirect) {
            return this.redirectToErrorPage();
        }

        const { classes } = this.props;

        return <Grid container={true} className={classes.gridRowFlex} spacing={2}>
            <Grid size={12}>
                <Typography className={classes.submissionTitle}>
                    Bulk Mark Final ({this.state.markFinalType.toUpperCase()})
                </Typography>
            </Grid>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <div className={classes.root}>
                            <Stepper activeStep={this.state.currentStep} orientation="vertical">
                                <Step key="SelectSubStep">
                                    <StepLabel>Select Submissions</StepLabel>
                                    <StepContent>
                                        {this.state.isSubsLoading ? (
                                            <LoadingDataTable titleText={"*"} count={8} />
                                        ) : (
                                            <Fade in={true} timeout={SeedTime}>
                                                <div>
                                                    <SharedMaterialReactTable
                                                        data={this.state.submissionData}
                                                        columns={this.getBulkMarkFinalColumns()}
                                                        title="Submissions"
                                                        isLoading={this.state.isSubsLoading}
                                                        enablePagination={true}
                                                        enableSorting={true}
                                                        enableFiltering={true}
                                                        enableColumnActions={true}
                                                        enableRowActions={false}
                                                        enableRowSelection={true}
                                                        enableMultiRowSelection={true}
                                                        enableSelectAll={true}
                                                        rowSelection={this.getRowSelectionState()}
                                                        isRowSelectable={(() => {
                                                            console.log('BulkMarkFinal: About to pass isRowSelectable prop:', {
                                                                methodExists: !!this.isRowSelectable,
                                                                methodType: typeof this.isRowSelectable,
                                                                methodValue: this.isRowSelectable
                                                            });
                                                            return this.isRowSelectable;
                                                        })()}
                                                        getRowId={(row, index) => `${row.submissionId}_${row.component}`}
                                                        onRowSelectionChange={(updatedSelection) => {
                                                            console.log('BulkMarkFinal onRowSelectionChange called with:', updatedSelection);
                                                            console.log('Type of updatedSelection:', typeof updatedSelection);
                                                            console.log('Keys:', Object.keys(updatedSelection));
                                                            console.log('Selected keys:', Object.keys(updatedSelection).filter(key => updatedSelection[key]));

                                                            // Only allow selectable rows to be selected
                                                            const selectedIndexes = Object.keys(updatedSelection)
                                                                .filter(key => updatedSelection[key])
                                                                    .flatMap(key => {
                                                                        const id = key;
                                                                        return this.state.submissionData.reduce((indices, item, index) => {
                                                                            if (`${item.submissionId}_${item.component}` === id) {
                                                                                indices.push(index);
                                                                            }
                                                                            return indices;
                                                                        }, []);
                                                                    });

                                                            console.log('Final selectedIndexes:', selectedIndexes);
                                                            this.setState({ selectedSubmissionIndexes: selectedIndexes });
                                                        }}
                                                        initialState={{
                                                            pagination: { pageIndex: 0, pageSize: 10 },
                                                        }}
                                                        muiPaginationProps={{
                                                            rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                                                        }}
                                                        emptyMessage="No submissions found."
                                                    />
                                                </div>
                                            </Fade>
                                        )}
                                        <div className={classes.actionsContainer}>
                                            <div>
                                                <Fab variant="extended" color="secondary" onClick={() => this.MarkFinalSelected()} disabled={this.state.selectedSubmissionIndexes.length === 0 || this.state.markFinalInProgress} className={classes.fab}>
                                                    Mark Final
                                                </Fab>
                                                {
                                                    this.state.isSaved ? <Typography variant='h6' color='primary'>
                                                        Your selections have been saved.
                                                    </Typography> : null
                                                }
                                            </div>
                                        </div>
                                    </StepContent>
                                </Step>
                            </Stepper>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
        //}
    }

    private async RefreshModel() {
        this.setState({ isSubsLoading: true });
        const subRetVal = await firstValueFrom(
            SubmissionsService.GetSubmissionsOfTypeForOrgsByAction$(
                this.props.user,
                [] as any,
                this.props.match.params.markFinalType,
                "markFinal"
            )
        );

        if (subRetVal instanceof AjaxError || !subRetVal) {
            this.setRedirectState();
        }
        else {
            //get hos interim data submission config value
            const retval = await firstValueFrom(
                AdministrationService.GetConfigKey$(
                    this.props.user,
                    HOS_ENABLE_INTERIM_SUBMISSION
                )
            );

            if (!retval) {
                this.setRedirectState();
            } else {
                this.setState({ enableHosInterimDataSubmission: retval.toLowerCase() === 'true' });
            }

            const subData: any[] = (subRetVal.response).map(item => {
                return {
                    submissionId: item.subId,
                    productLine: item.productLine,
                    component: item.component,
                    orgId: item.orgId,
                    orgName: item.orgName,
                    validationStatus: item.validationStatus,
                    isAttested: item.isAttested ? 'Yes' : 'No',
                    isFinal: item.isFinal ? 'Yes' : 'No'
                };
            });

            this.setState({
                submissionData: subData
            });
        }
        this.setState({ isSubsLoading: false });
    }

    private async ResetState() {
        this.setState({
            currentStep: 0,
            orgData: [],
            selectedOrganizationIndexes: [],
            submissionData: [],
            selectedSubmissionIndexes: [],
            markFinalType: this.props.match.params.markFinalType,
            isSaved: false
        });
        await this.RefreshModel();
    }

    private MarkFinalSelected() {
        const submissions: ISelectedSubmissions[] = this.state.selectedSubmissionIndexes.map(i => {
            let sub = this.state.submissionData[i];

            return {
                submissionId: sub.submissionId,
                productLine: sub.productLine,
                component: sub.component
            };
        });

        switch (this.state.markFinalType) {
            case SubmissionType.HEDIS:
                throw new Error("Not Implemented");
            case SubmissionType.CAHPS:
            case SubmissionType.HOS:
                this.BulkMarkFinal({
                    submissions: submissions,
                    fileTypes: []
                });
                break;
        }
    }

    private async BulkMarkFinal(bulkSubs: ISelectedSubmissionsHolder) {
        this.setState({ markFinalInProgress: true });
        this.setState({ isSaved: false });
        const retval = await firstValueFrom(
            SubmissionsService.SubmissionsBulkAction$(
                this.props.user,
                bulkSubs,
                "MarkFinal"
            )
        );
        if (retval instanceof AjaxError || !retval) {
            this.setRedirectState();
        }
        else {
            this.RefreshModel();
            this.setState({ selectedSubmissionIndexes: [], isSaved: true }, () => this.setState({ markFinalInProgress: false }));
        }
    }
}

// REDUX CONTAINER
const BulkMarkFinalViewWithState = connect<IGenericUserProps>(genericMapStateToProps)(BulkMarkFinalView);

// STYLES CONTAINER
const BulkMarkFinalPage = withStyles(styles)(BulkMarkFinalViewWithState);

export default BulkMarkFinalPage;
